import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, BookOpen, CheckCircle2, Eye, EyeOff, KeyRound, LifeBuoy, MapPin, RotateCcw, Save, ShieldCheck } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx } from "../../utils";
import EmptyState from "../../components/ui/EmptyState";
import Input, { Label, Select } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";

// Fields whose key hints at a secret are masked by default.
const SECRET_HINT = /(secret|token|password|api[_-]?key|auth|private)/i;

// The server never sends a saved secret back in full — it arrives as these
// dots plus the last four characters, and is kept as-is if saved unchanged.
const MASK = "••••••••";
const isMasked = (value) => String(value || "").startsWith(MASK);

// A field is required unless its help text opens with "Optional".
const isRequired = (field) => !/^optional\b/i.test(field.short_description || "");

const CredentialField = ({ field, value, onChange, saved }) => {
  const secret = SECRET_HINT.test(field.key || "");
  const [revealed, setRevealed] = useState(false);
  const id = `cred-${field.key}`;
  const filled = String(value || "").trim().length > 0;
  const masked = secret && isMasked(value);

  // Typing into the masked stand-in would leave the dots in front of the new
  // key, and the server ignores anything that still starts with them. So a
  // keystroke after the mask keeps only what was typed, and any other edit
  // clears the field for a fresh paste.
  const handleChange = (next) => {
    if (masked && next.startsWith(value)) return onChange(field.key, next.slice(value.length));
    if (masked && isMasked(next)) return onChange(field.key, "");
    onChange(field.key, next);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} required={isRequired(field)}>
          {field.label || field.key}
        </Label>
        {filled && saved && (
          <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-emerald-600">
            <CheckCircle2 size={12} /> Saved
          </span>
        )}
      </div>
      {field.type === "select" ? (
        // A choice from a list the admin manages, e.g. the default AI model.
        // Empty means "use the recommended default" on an optional field.
        <div className="relative mt-1.5">
          <Select
            id={id}
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            className="h-11 text-[13px]"
            aria-describedby={field.short_description ? `${id}-hint` : undefined}
          >
            <option value="">{isRequired(field) ? "Choose one…" : "Recommended default"}</option>
            {(field.options || []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label || option.value}
              </option>
            ))}
          </Select>
        </div>
      ) : (
      <div className="relative mt-1.5">
        <Input
          id={id}
          type={secret && !revealed && !masked ? "password" : "text"}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={(e) => masked && e.target.select()}
          placeholder={`Paste your ${field.label || field.key || "value"}`}
          autoComplete="off"
          spellCheck={false}
          className={cx("h-11 font-mono text-[13px]", secret && "pr-11")}
          aria-describedby={field.short_description ? `${id}-hint` : undefined}
        />
        {secret && !masked && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className="absolute right-1.5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700"
            aria-label={revealed ? "Hide value" : "Show value"}
          >
            {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
      )}
      {masked && (
        <p className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-relaxed text-ink-500">
          <ShieldCheck size={12} className="mt-0.5 shrink-0 text-ink-400" aria-hidden />
          Hidden for security. Paste a new value to replace it.
        </p>
      )}
      {field.short_description && (
        <p id={`${id}-hint`} className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-relaxed text-ink-500">
          <MapPin size={12} className="mt-0.5 shrink-0 text-ink-400" aria-hidden />
          {field.short_description}
        </p>
      )}
    </div>
  );
};

/**
 * The values the app needs from the provider, with where each comes from
 * beside the form. Saved state is shown per field and for the form as a whole.
 */
const CredentialsPanel = ({ app, appId, locationId, companyId, onSaved, onOpenGuide, onOpenSupport }) => {
  const { installs } = useAppServices();
  const toast = useToast();

  const fields = useMemo(() => app?.formFields || [], [app]);

  const [values, setValues] = useState({});
  const [saved, setSaved] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const query = useMemo(
    () =>
      new URLSearchParams({
        appId,
        ...(locationId && { locationId }),
        ...(companyId && { companyId }),
      }).toString(),
    [appId, locationId, companyId],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));
      const { response } = await installs.GetCredentials(query);
      if (!alive) return;
      const next = { ...blank, ...(response?.credentials || {}) };
      setValues(next);
      setSaved(next);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, fields]);

  const onChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const dirty = useMemo(() => fields.some((f) => (values[f.key] || "") !== (saved[f.key] || "")), [fields, values, saved]);
  const complete = fields.filter(isRequired).every((f) => String(saved[f.key] || "").trim());

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { error } = await installs.SaveCredentials({
      appId,
      locationId: locationId || undefined,
      companyId: companyId || undefined,
      credentials: values,
    });
    setSaving(false);
    if (error) {
      toast(error?.message || "Could not save credentials", "error");
      return;
    }
    setSaved(values);
    toast("Credentials saved");
    onSaved?.(values);
  };

  if (fields.length === 0) {
    return (
      <div className="rounded-2xl border border-ink-200/70 bg-white shadow-card">
        <EmptyState
          tone="emerald"
          icon={<KeyRound size={24} />}
          title="No credentials needed"
          description="This app doesn't need any values from you, so there's nothing to configure here."
        />
      </div>
    );
  }

  const status = dirty
    ? { label: "Unsaved changes", cls: "bg-amber-50 text-amber-700 ring-amber-200/80", dot: "bg-amber-500" }
    : complete
      ? { label: "Connected", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200/80", dot: "bg-emerald-500" }
      : { label: "Not set up yet", cls: "bg-ink-100 text-ink-600 ring-ink-200/80", dot: "bg-ink-400" };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] 2xl:grid-cols-[minmax(0,1fr)_24rem]">
      <form onSubmit={onSubmit} className="min-w-0 self-start overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
        <div className="flex flex-wrap items-center gap-3 border-b border-ink-150 px-6 py-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
            <KeyRound size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[16px] font-semibold tracking-tight text-ink-900">Connect your account</h2>
            <p className="text-[12.5px] text-ink-500">Saved for this account and used every time the app runs.</p>
          </div>
          <span className={cx("inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold ring-1", status.cls)}>
            <span className={cx("h-1.5 w-1.5 rounded-full", status.dot)} />
            {status.label}
          </span>
        </div>

        <div className="space-y-6 px-6 py-6">
          {loading
            ? fields.map((f) => (
                <div key={f._id || f.key} className="space-y-2">
                  <div className="h-4 w-28 animate-pulse rounded bg-ink-100" />
                  <div className="h-11 w-full animate-pulse rounded-lg bg-ink-100" />
                </div>
              ))
            : fields.map((field) => (
                <CredentialField
                  key={field._id || field.key}
                  field={field}
                  value={values[field.key] ?? ""}
                  onChange={onChange}
                  saved={(values[field.key] || "") === (saved[field.key] || "")}
                />
              ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-ink-150 bg-ink-50/50 px-6 py-3.5">
          <p className="mr-auto flex items-center gap-1.5 text-[11.5px] text-ink-500">
            <ShieldCheck size={13} className="text-ink-400" aria-hidden />
            Only used to talk to your provider on your behalf.
          </p>
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={() => setValues(saved)}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl px-3.5 text-[13px] font-medium text-ink-600 transition-colors hover:bg-ink-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <RotateCcw size={14} /> Reset
          </button>
          <button
            type="submit"
            disabled={!dirty || saving}
            className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 px-4 text-[13px] font-semibold text-white shadow-brand-glow transition-all hover:brightness-110 disabled:opacity-50 disabled:shadow-none disabled:hover:brightness-100"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save credentials"}
          </button>
        </div>
      </form>

      <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
        <section className="rounded-2xl border border-ink-200/70 bg-white p-5 shadow-card">
          <h3 className="text-[13.5px] font-semibold text-ink-900">Where to find each value</h3>
          <ol className="mt-4 space-y-4">
            {fields.map((f, i) => (
              <li key={f._id || f.key} className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-50 text-[11.5px] font-semibold text-brand-700 ring-1 ring-brand-200/70">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-ink-800">{f.label || f.key}</p>
                  <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">{f.short_description || "From your provider account."}</p>
                </div>
              </li>
            ))}
          </ol>
          {onOpenGuide && (
            <button
              type="button"
              onClick={onOpenGuide}
              className="mt-5 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-ink-200 bg-white text-[13px] font-medium text-ink-700 shadow-sm transition-colors hover:bg-ink-50"
            >
              <BookOpen size={14} /> Full setup guide
            </button>
          )}
        </section>

        {onOpenSupport && (
          <section className="rounded-2xl border border-ink-200/70 bg-white p-5 shadow-card">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
                <LifeBuoy size={16} />
              </span>
              <div>
                <h3 className="text-[13.5px] font-semibold text-ink-900">Can't find a value?</h3>
                <p className="text-[12px] text-ink-500">We'll point you to it.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onOpenSupport}
              className="mt-4 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-ink-200 bg-white text-[13px] font-medium text-ink-700 shadow-sm transition-colors hover:bg-ink-50"
            >
              Ask support <ArrowRight size={13} />
            </button>
          </section>
        )}
      </aside>
    </div>
  );
};

export default CredentialsPanel;
